/* eslint-disable no-alert */
import React from 'react';
import { Query, Mutation } from '@apollo/client/react/components';
import PropTypes from 'prop-types';
import reactNg from 'react-ng';
import { openModal } from 'modal-core-components';
import { Modal } from 'modal-components';
import { GET_CURRENT_USER } from 'client-web-app/queries';
import { GET_CLIENT_COMPANY, GET_CLIENT_NOTIFICATIONS, DELETE_NOTIFICATION } from './queries';
import getNotificationsFromResponseData from './getNotificationsFromResponseData';
import SidebarCompanyHeader from './SidebarCompanyHeader';

/**
 * @typedef {Object} Notification
 * @property {Array.<Object>} assets
 * @property {Object} context
 * @property {String} personId
 * @property {Object} payload
 * @property {Object} resources
 *
 * @callback DismissNotificationHandler
 * @param {Notification} notification
 * @returns {Promise}
 *
 * Opens our React modal with an Angular bl-notifications-list component, and
 * passes through our manicured data and callback 💅
 * @param {String} companyName
 * @param {Array.<Notification>} notifications
 * @param {DismissNotificationHandler} onDismissNotification
 */ const openNotificationsListModal = (companyName, notifications, onDismissNotification) =>
  openModal(closeModal => (
    <Modal size="large" animateIn>
      <Modal.Header title="Notifications" onClose={closeModal} />
      {reactNg({
        vm: {
          companyName,
          notifications: {
            data: notifications, // conform to `collection.data` convention for angular
          },
          closeModal,
          onDismissNotification,
        },
      })`
        <bl-notification-list
          action-cb="vm.closeModal"
          client-id="vm.clientId"
          notification-list="vm.notifications"
          on-dismiss-notification="vm.onDismissNotification"
        ></bl-notification-list>
      `}
    </Modal>
  ));

/**
 * Fetches the client's company name and notifications data, then packages up
 * an onClick handler for spawning a modal with the detail view of the data.
 */ const SidebarCompanyHeaderContainer = ({ clientId }) => (
  <Query query={GET_CLIENT_COMPANY} variables={{ clientId }}>
    {({ loading: loadingCompany, data: companyData }) => {
      if (loadingCompany || !companyData)
        return (
          <SidebarCompanyHeader
            dataTestId={`sidebar-company-header-${clientId}`}
            companyName=""
            notificationCount={0}
          />
        );
      const { company } = companyData.client;
      const args = `?cids=${clientId}&full=true`;

      return (
        <Query query={GET_CURRENT_USER}>
          {({ data: person, loading, error }) => {
            if (loading || error || !person) {
              return (
                <SidebarCompanyHeader
                  dataTestId={`sidebar-company-header-${clientId}`}
                  companyName={company}
                  notificationCount={0}
                />
              );
            }
            const personId = person.currentUser.id;
            return (
              <Query query={GET_CLIENT_NOTIFICATIONS} variables={{ personId, args }}>
                {({ loading: loadingNotifications, data: notificationsData }) => {
                  if (loadingNotifications || !notificationsData) {
                    return (
                      <SidebarCompanyHeader
                        dataTestId={`sidebar-company-header-${clientId}`}
                        companyName={company}
                        notificationCount={0}
                      />
                    );
                  }
                  const notifications = getNotificationsFromResponseData(notificationsData);
                  const refetchQueries = [
                    { query: GET_CLIENT_NOTIFICATIONS, variables: { personId, args } },
                  ];

                  return (
                    <Mutation mutation={DELETE_NOTIFICATION} refetchQueries={refetchQueries}>
                      {deleteNotification => {
                        const dismissNotificationHandler = async event => {
                          try {
                            await deleteNotification({
                              variables: {
                                personId,
                                eventId: event.id,
                              },
                            });
                          } catch (err) {
                            window.alert(
                              'Sorry, there was an error dismissing this notification. Please contact us if this issue persists.',
                            );
                          }
                        };
                        return (
                          <SidebarCompanyHeader
                            dataTestId={`sidebar-company-header-${clientId}`}
                            companyName={company}
                            notificationCount={notifications.length}
                            onClick={() =>
                              openNotificationsListModal(
                                company,
                                notifications,
                                dismissNotificationHandler,
                              )
                            }
                          />
                        );
                      }}
                    </Mutation>
                  );
                }}
              </Query>
            );
          }}
        </Query>
      );
    }}
  </Query>
);

SidebarCompanyHeaderContainer.propTypes = {
  clientId: PropTypes.string.isRequired,
};

export default SidebarCompanyHeaderContainer;
